import { POLICY_PACK } from "../data/cdmPolicyPack";
import { assessProject, isNotifiable } from "./assessmentEngine";
import type {
  ControlledDeliverable,
  DeliverableSection,
  DeliverableType,
  ProjectWorkspace,
} from "../types/domain";

interface TemplateSection {
  id: string;
  title: string;
  requirement: string;
  build: (workspace: ProjectWorkspace) => {
    content: string;
    sourceEntityIds: string[];
    evidenceIds: string[];
  };
}

interface DeliverableDefinition {
  type: DeliverableType;
  title: string;
  regulation: string;
  applicable: (workspace: ProjectWorkspace) => boolean;
  sections: TemplateSection[];
}

const projectBasis = (workspace: ProjectWorkspace) => ({
  content: [
    `Project: ${workspace.project.name || "Not confirmed"}`,
    `Project number: ${workspace.project.projectNumber || "Not confirmed"}`,
    `Client: ${workspace.project.clientName || "Not confirmed"}`,
    `Location: ${workspace.project.location || "Not confirmed"}`,
    `Work type: ${workspace.project.workType}`,
    `Current phase: ${workspace.project.currentPhase || "Not confirmed"}`,
    `Description: ${workspace.project.description || "Not confirmed"}`,
  ].join("\n"),
  sourceEntityIds: [workspace.project.id],
  evidenceIds: [],
});

const appointments = (workspace: ProjectWorkspace) => {
  const records = workspace.evidence.filter((item) => item.type === "appointment");
  return {
    content:
      records
        .map(
          (item) =>
            `${item.title} — ${item.status}; owner ${item.owner || "unassigned"}; reference ${item.reference || "missing"}`,
        )
        .join("\n") || "No appointment evidence is recorded.",
    sourceEntityIds: records.map((item) => item.id),
    evidenceIds: records.map((item) => item.id),
  };
};

const designRisks = (workspace: ProjectWorkspace) => ({
  content:
    workspace.risks
      .map(
        (risk) =>
          `${risk.designElement || "Untitled element"} — ${risk.hazard || "hazard missing"}; decision: ${risk.designDecision || "missing"}; residual risk: ${risk.residualRisk}; owner: ${risk.owner || "unassigned"}; review: ${risk.reviewStatus}`,
      )
      .join("\n") || "No design risks are recorded.",
  sourceEntityIds: workspace.risks.map((item) => item.id),
  evidenceIds: Array.from(new Set(workspace.risks.flatMap((item) => item.evidenceIds))),
});

const hirarc = (workspace: ProjectWorkspace) => ({
  content:
    workspace.hirarcAssessments
      .map(
        (item) =>
          `${item.activity || "Untitled activity"} — initial ${item.riskScore}/25; residual ${item.residualRiskScore}/25; control: ${item.recommendedControls || "missing"}; PIC: ${item.owner || "unassigned"}; status: ${item.status}`,
      )
      .join("\n") || "No HIRARC assessments are recorded.",
  sourceEntityIds: workspace.hirarcAssessments.map((item) => item.id),
  evidenceIds: Array.from(
    new Set(workspace.hirarcAssessments.flatMap((item) => item.evidenceIds)),
  ),
});

const controls = (workspace: ProjectWorkspace) => ({
  content:
    workspace.criticalControls
      .map(
        (item) =>
          `${item.title || "Untitled control"} — ${item.controlType}; standard: ${item.performanceStandard || "missing"}; verification: ${item.verificationMethod || "missing"}; health: ${item.health}`,
      )
      .join("\n") || "No critical controls are recorded.",
  sourceEntityIds: workspace.criticalControls.map((item) => item.id),
  evidenceIds: Array.from(
    new Set(workspace.criticalControls.flatMap((item) => item.evidenceIds)),
  ),
});

const revisions = (workspace: ProjectWorkspace) => ({
  content:
    workspace.documentRevisions
      .map(
        (item) =>
          `${item.documentNumber || "No number"} ${item.revision} — ${item.title || "untitled"}; ${item.status}; review ${item.reviewStatus}; checksum ${item.checksum || "not uploaded"}`,
      )
      .join("\n") || "No controlled document revisions are recorded.",
  sourceEntityIds: workspace.documentRevisions.map((item) => item.id),
  evidenceIds: [],
});

const evidenceIndex = (workspace: ProjectWorkspace) => ({
  content:
    workspace.evidence
      .map(
        (item) =>
          `${item.reference || "No reference"} — ${item.title || "untitled"}; ${item.status}; version ${item.version || "not stated"}`,
      )
      .join("\n") || "No evidence is recorded.",
  sourceEntityIds: workspace.evidence.map((item) => item.id),
  evidenceIds: workspace.evidence.map((item) => item.id),
});

const dutyActions = (workspace: ProjectWorkspace) => {
  const assessment = assessProject(workspace.project);
  return {
    content: assessment.triggeredRules
      .map((rule) => {
        const action = workspace.dutyActions.find((item) => item.ruleId === rule.id);
        return `${rule.regulation} ${rule.title} — ${action?.status ?? "open"}; owner ${action?.owner || "unassigned"}; response ${action?.response || "not recorded"}`;
      })
      .join("\n"),
    sourceEntityIds: assessment.triggeredRules.map((item) => item.id),
    evidenceIds: Array.from(new Set(workspace.dutyActions.flatMap((item) => item.evidenceIds))),
  };
};

const section = (
  id: string,
  title: string,
  requirement: string,
  build: TemplateSection["build"],
): TemplateSection => ({ id, title, requirement, build });

export const DELIVERABLE_DEFINITIONS: DeliverableDefinition[] = [
  {
    type: "pci",
    title: "Pre-construction information pack",
    regulation: "CDM Regulations 4, 5 and 11",
    applicable: () => true,
    sections: [
      section("basis", "Project brief and scope", "Confirmed project identity, scope and constraints.", projectBasis),
      section("appointments", "Dutyholder appointments", "Written appointments and accepted responsibilities.", appointments),
      section("existing", "Existing information and surveys", "Available surveys, records and known site information.", evidenceIndex),
      section("hazards", "Significant hazards and constraints", "Known design, site, operational and adjacent hazards.", designRisks),
      section("rules", "Regulatory and approval gateways", "Applicable duties and project-specific gateways.", dutyActions),
    ],
  },
  {
    type: "design-risk-assessment",
    title: "Design risk assessment",
    regulation: "CDM Regulations 11 and 12",
    applicable: (workspace) => workspace.project.hasDesignWork,
    sections: [
      section("basis", "Design basis", "Project and design scope.", projectBasis),
      section("risk", "Design risk register", "Elimination, reduction and residual-risk decisions.", designRisks),
      section("hirarc", "HIRARC interface", "Quantified activity risks requiring design coordination.", hirarc),
      section("controls", "Critical design controls", "Performance standards and verification methods.", controls),
      section("revisions", "Controlled design information", "Exact revisions carrying each safety decision.", revisions),
    ],
  },
  {
    type: "principal-designer-appointment",
    title: "Principal designer written appointment",
    regulation: "CDM Regulation 7",
    applicable: (workspace) => (workspace.project.contractorCount ?? 0) > 1,
    sections: [
      section("basis", "Project and appointing client", "Identity of project and client.", projectBasis),
      section("scope", "Appointment scope", "Coordination, design-risk and Safety and Health File duties.", () => ({
        content:
          "Coordinate pre-construction health and safety; ensure designers comply; maintain the design-risk evidence trail; liaise with the principal contractor; prepare, review and update the Safety and Health File.",
        sourceEntityIds: ["MY-CDM-R7", "MY-CDM-R12", "MY-CDM-R14"],
        evidenceIds: [],
      })),
      section("competence", "Capability evidence", "Skills, knowledge, training and organisational capability.", (workspace) => ({
        content:
          workspace.competenceRecords
            .filter((item) => /designer/i.test(item.role))
            .map((item) => `${item.personName} — ${item.status}; ${item.evidenceReference}`)
            .join("\n") || "No principal designer competence evidence is recorded.",
        sourceEntityIds: workspace.competenceRecords.map((item) => item.id),
        evidenceIds: [],
      })),
      section("acceptance", "Acceptance and signatures", "Named client and appointee acceptance.", appointments),
    ],
  },
  {
    type: "principal-contractor-appointment",
    title: "Principal contractor written appointment",
    regulation: "CDM Regulation 7",
    applicable: (workspace) => (workspace.project.contractorCount ?? 0) > 1,
    sections: [
      section("basis", "Project and appointing client", "Identity of project and client.", projectBasis),
      section("scope", "Appointment scope", "Construction planning, coordination and consultation duties.", () => ({
        content:
          "Plan, manage and monitor the construction phase; coordinate contractors; prepare and maintain the construction phase plan; provide welfare; consult workers; provide information to the principal designer for the Safety and Health File.",
        sourceEntityIds: ["MY-CDM-R7", "MY-CDM-R13", "MY-CDM-R17"],
        evidenceIds: [],
      })),
      section("competence", "Capability evidence", "Skills, knowledge, training and organisational capability.", (workspace) => ({
        content:
          workspace.competenceRecords
            .filter((item) => /contractor/i.test(item.role))
            .map((item) => `${item.personName} — ${item.status}; ${item.evidenceReference}`)
            .join("\n") || "No principal contractor competence evidence is recorded.",
        sourceEntityIds: workspace.competenceRecords.map((item) => item.id),
        evidenceIds: [],
      })),
      section("acceptance", "Acceptance and signatures", "Named client and appointee acceptance.", appointments),
    ],
  },
  {
    type: "jkkp12-notification",
    title: "JKKP 12 project notification working record",
    regulation: "CDM Regulation 8",
    applicable: (workspace) => isNotifiable(workspace.project),
    sections: [
      section("basis", "Project particulars", "Notification project identity and location.", projectBasis),
      section("programme", "Programme and scale", "Dates, working days, person-days and workforce.", (workspace) => ({
        content: `Start: ${workspace.project.startDate || "missing"}\nEnd: ${workspace.project.endDate || "missing"}\nWorking days: ${workspace.project.workingDays ?? "missing"}\nPerson-days: ${workspace.project.personDays ?? "missing"}\nPeak workers: ${workspace.project.peakWorkers ?? "missing"}\nContractors: ${workspace.project.contractorCount ?? "missing"}`,
        sourceEntityIds: [workspace.project.id],
        evidenceIds: [],
      })),
      section("dutyholders", "Named dutyholders", "Client, designers and contractors.", appointments),
      section("status", "Submission control", "Authorised submission reference and display record.", (workspace) => ({
        content:
          workspace.evidence
            .filter((item) => /JKKP|notification/i.test(`${item.title} ${item.reference}`))
            .map((item) => `${item.reference} — ${item.status}`)
            .join("\n") || "No authorised notification evidence is recorded.",
        sourceEntityIds: workspace.evidence.map((item) => item.id),
        evidenceIds: workspace.evidence.map((item) => item.id),
      })),
    ],
  },
  {
    type: "construction-phase-plan",
    title: "Construction phase plan",
    regulation: "CDM Regulation 13",
    applicable: () => true,
    sections: [
      section("basis", "Project and construction scope", "Construction phase basis.", projectBasis),
      section("management", "Management arrangements", "Roles, communication, consultation and coordination.", appointments),
      section("hirarc", "Risk-control plan", "Activity HIRARC and residual-risk decisions.", hirarc),
      section("controls", "Critical control standards", "Controls that prevent or mitigate fatal events.", controls),
      section("emergency", "Emergency and incident arrangements", "Response, reporting and learning arrangements.", (workspace) => ({
        content:
          workspace.incidents
            .map((item) => `${item.title} — lesson: ${item.lessons || "not recorded"}`)
            .join("\n") || "Emergency arrangements must be completed for the project.",
        sourceEntityIds: workspace.incidents.map((item) => item.id),
        evidenceIds: [],
      })),
      section("monitoring", "Inspection and review", "Control verification and review triggers.", revisions),
    ],
  },
  {
    type: "safety-health-file",
    title: "Safety and Health File",
    regulation: "CDM Regulation 14",
    applicable: (workspace) => (workspace.project.contractorCount ?? 0) > 1,
    sections: [
      section("basis", "Project record", "Final project identity and scope.", projectBasis),
      section("residual", "Residual design risks", "Risks relevant to future use, maintenance and demolition.", (workspace) => ({
        content:
          workspace.risks
            .filter((item) => item.lifecycleStage !== "construction")
            .map((item) => `${item.lifecycleStage}: ${item.hazard} — ${item.control}`)
            .join("\n") || "No post-construction residual risks are recorded.",
        sourceEntityIds: workspace.risks.map((item) => item.id),
        evidenceIds: workspace.risks.flatMap((item) => item.evidenceIds),
      })),
      section("controls", "Permanent safety systems", "Critical controls requiring inspection or maintenance.", controls),
      section("drawings", "As-built safety information", "Approved drawings, specifications and model revisions.", revisions),
      section("evidence", "Certificates and records", "Inspection, test, commissioning and handover evidence.", evidenceIndex),
      section("change", "Change and issue history", "Revision comparisons and unresolved safety issues.", (workspace) => ({
        content: `${workspace.revisionComparisons.length} revision comparison(s); ${workspace.safetyIssuePins.filter((item) => item.status !== "complete").length} open page/BCF issue(s).`,
        sourceEntityIds: [
          ...workspace.revisionComparisons.map((item) => item.id),
          ...workspace.safetyIssuePins.map((item) => item.id),
        ],
        evidenceIds: [],
      })),
    ],
  },
];

const sectionStatus = (
  sectionValue: ReturnType<TemplateSection["build"]>,
): DeliverableSection["status"] => {
  const content = sectionValue.content.toLowerCase();
  if (
    !sectionValue.content.trim() ||
    content.includes("no ") ||
    content.includes("missing") ||
    content.includes("not confirmed") ||
    content.includes("must be completed")
  )
    return "missing";
  if (content.includes("unassigned") || content.includes("not recorded")) return "incomplete";
  return "ready";
};

export function compileDeliverable(
  type: DeliverableType,
  workspace: ProjectWorkspace,
  existing?: ControlledDeliverable,
): ControlledDeliverable {
  const definition = DELIVERABLE_DEFINITIONS.find((item) => item.type === type);
  if (!definition) throw new Error(`Unknown deliverable type: ${type}`);
  const sections = definition.sections.map((template) => {
    const value = template.build(workspace);
    return {
      id: template.id,
      title: template.title,
      requirement: template.requirement,
      ...value,
      status: sectionStatus(value),
    };
  });
  const missing = sections.filter((item) => item.status === "missing").length;
  return {
    id: existing?.id ?? crypto.randomUUID(),
    type,
    title: definition.title,
    reference:
      existing?.reference ||
      `${workspace.project.projectNumber || "DSM"}-${type.toUpperCase().replaceAll("-", "-")}`,
    revision: existing?.revision ?? "P01",
    status: missing === 0 ? (existing?.status === "issued" ? "issued" : "draft") : "draft",
    owner: existing?.owner ?? "",
    reviewer: existing?.reviewer ?? "",
    generatedAt: new Date().toISOString(),
    issuedAt: existing?.issuedAt ?? "",
    policyPackVersion: POLICY_PACK.version,
    sections,
    limitationNotes: existing?.limitationNotes ?? "",
    approvalId: existing?.approvalId ?? "",
  };
}

export function compileApplicableDeliverables(
  workspace: ProjectWorkspace,
): ControlledDeliverable[] {
  return DELIVERABLE_DEFINITIONS.filter((item) => item.applicable(workspace)).map(
    (definition) =>
      compileDeliverable(
        definition.type,
        workspace,
        workspace.deliverables.find((item) => item.type === definition.type),
      ),
  );
}

export function deliverableHtml(
  deliverable: ControlledDeliverable,
  workspace: ProjectWorkspace,
): string {
  const sections = deliverable.sections
    .map(
      (sectionValue, index) => `
        <section>
          <h2>${index + 1}. ${escapeHtml(sectionValue.title)}</h2>
          <p class="requirement">${escapeHtml(sectionValue.requirement)}</p>
          <pre>${escapeHtml(sectionValue.content)}</pre>
          <p class="status">Section status: ${sectionValue.status}</p>
        </section>`,
    )
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(deliverable.title)}</title>
  <style>body{font:15px/1.55 Arial,sans-serif;color:#102a27;max-width:900px;margin:40px auto;padding:0 32px}header{border-bottom:3px solid #0d7d72;padding-bottom:20px}h1{font-size:30px}h2{font-size:19px;margin-top:30px}.meta{display:grid;grid-template-columns:repeat(3,1fr);gap:12px}.meta div{background:#f2f7f5;padding:10px}.requirement,.status{color:#526a66}pre{white-space:pre-wrap;font:14px/1.5 Arial,sans-serif}footer{margin-top:40px;border-top:1px solid #ccd8d5;padding-top:15px;color:#526a66}@media print{body{margin:0}}</style></head>
  <body><header><small>DESIGN SAFETY MY · CONTROLLED OUTPUT</small><h1>${escapeHtml(deliverable.title)}</h1>
  <div class="meta"><div><b>Project</b><br>${escapeHtml(workspace.project.name)}</div><div><b>Reference</b><br>${escapeHtml(deliverable.reference)}</div><div><b>Revision</b><br>${escapeHtml(deliverable.revision)}</div><div><b>Status</b><br>${escapeHtml(deliverable.status)}</div><div><b>Owner</b><br>${escapeHtml(deliverable.owner || "Unassigned")}</div><div><b>Policy pack</b><br>${escapeHtml(deliverable.policyPackVersion)}</div></div></header>
  ${sections}<footer>Generated ${escapeHtml(deliverable.generatedAt)}. This output remains subject to named professional review and the limitations recorded in the project workspace.</footer></body></html>`;
}

const escapeHtml = (value: string) =>
  value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
